import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import { useGlobalState, useQueryParams, useTooltip } from '../../hooks';
import { Asset } from '../../helpers';
import {
  routesConfig,
  DatasetCrumbContent,
  EntityCrumbContent,
  GlossaryCrumbContent,
} from '../../routesConfig';

import './Breadcrumbs.css';

/**
 * Inserts parent-dataset crumbs before the current dataset crumb when viewing a
 * nested dataset (or a field within one), so the trail reflects the actual
 * `parent_dataset_id` hierarchy. URLs are unchanged: each parent links to the same
 * route with only the dataset_id segment swapped. Returns the array untouched when
 * there is no dataset crumb, or no resolved ancestors for the dataset on screen.
 * @param {Array} breadcrumbs - The crumbs produced by useBreadcrumbs.
 * @param {{ dataset_id: number, ancestors: object[] }} datasetAncestors - Resolved chain.
 * @returns {Array} The (possibly) augmented crumb list.
 */
const padBreadcrumbsWithAncestors = (breadcrumbs, datasetAncestors) => {
  const ancestors = datasetAncestors?.ancestors;
  if (!ancestors?.length) return breadcrumbs;

  // The dataset crumb has a dataset_id but is not the field crumb.
  const datasetIndex = breadcrumbs.findIndex(
    ({ match }) => match.params?.dataset_id && !match.params?.field_id
  );
  if (datasetIndex === -1) return breadcrumbs;

  const { params } = breadcrumbs[datasetIndex].match;
  // Only pad when the resolved chain belongs to the dataset currently on screen
  // (guards against a stale response from a previously viewed dataset).
  if (String(datasetAncestors.dataset_id) !== String(params.dataset_id)) return breadcrumbs;

  const ancestorCrumbs = ancestors.map(ancestor => ({
    key: `dataset-ancestor-${ancestor.dataset_id}`,
    // Same route, only the trailing dataset_id segment changes -> URLs stay as today.
    match: {
      pathname: `/systems/${params.system_id}/${params.datasource_id}/${params.dataset_group_id}/${ancestor.dataset_id}`,
    },
    breadcrumb: <DatasetCrumbContent dataset={ancestor} />,
  }));

  return [
    ...breadcrumbs.slice(0, datasetIndex),
    ...ancestorCrumbs,
    ...breadcrumbs.slice(datasetIndex),
  ];
};

/**
 * Inserts parent-entity crumbs before the current entity crumb when viewing a
 * nested entity, so the trail reflects the actual `parent_entity_id` hierarchy.
 * URLs are unchanged: each parent links to the same route with only the entity_id
 * segment swapped. Returns the array untouched when there is no entity crumb, or no
 * resolved ancestors for the entity on screen.
 * @param {Array} breadcrumbs - The crumbs produced by useBreadcrumbs.
 * @param {{ entity_id: number, ancestors: object[] }} entityAncestors - Resolved chain.
 * @returns {Array} The (possibly) augmented crumb list.
 */
const padBreadcrumbsWithEntityAncestors = (breadcrumbs, entityAncestors) => {
  const ancestors = entityAncestors?.ancestors;
  if (!ancestors?.length) return breadcrumbs;

  const entityIndex = breadcrumbs.findIndex(({ match }) => match.params?.entity_id);
  if (entityIndex === -1) return breadcrumbs;

  const { params } = breadcrumbs[entityIndex].match;
  // Only pad when the resolved chain belongs to the entity currently on screen
  // (guards against a stale response from a previously viewed entity).
  if (String(entityAncestors.entity_id) !== String(params.entity_id)) return breadcrumbs;

  const ancestorCrumbs = ancestors.map(ancestor => ({
    key: `entity-ancestor-${ancestor.entity_id}`,
    // Same route, only the trailing entity_id segment changes -> URLs stay as today.
    match: {
      pathname: `/glossaries/${params.glossary_id}/${ancestor.entity_id}`,
    },
    breadcrumb: <EntityCrumbContent name={ancestor.entity_name} />,
  }));

  return [
    ...breadcrumbs.slice(0, entityIndex),
    ...ancestorCrumbs,
    ...breadcrumbs.slice(entityIndex),
  ];
};

/**
 * Rewrites the `/attributes` crumb into the attribute's place in its glossary.
 *
 * An attribute keeps the short `/attributes/:id` URL as a shortcut, which on its own would produce
 * "Home > All Attributes > Customer Name" — the flat list of every attribute — rather than saying
 * anything about where the asset actually lives. The trail is rebuilt from the hierarchy instead:
 * "Home > Glossaries > My Glossary > Customer > Customer Name", the same shape an entity's trail has,
 * at any depth.
 *
 * Only the first crumb reflects how the attribute was reached, and only as a way back to where the
 * user was: opened from the All Attributes tab it reads "All Attributes > My Glossary > Customer >
 * Customer Name", where a plain "Glossaries" would lead somewhere they had not been. Everything after
 * it is the asset's place in the model, so the path never depends on the route in — which is what went
 * wrong when the whole trail followed the origin and a nested attribute lost its parents.
 *
 * The origin comes from the `from` that DataTable puts on every row link, so it is present exactly when
 * there is history to go back to. A deep link, a bookmark or a reload has none and gets "Glossaries".
 *
 * The entities between the glossary and the attribute come from the resolved ancestor chain, so an
 * attribute nested under "Customer" shows it and everything above it. An attribute placed directly in a
 * glossary has none, and its trail is the glossary alone.
 *
 * An attribute value is one hop further on and gets the same crumbs, since a value sits under its
 * attribute: its own crumb simply follows the attribute's. Its `from` is the attribute page, so the
 * origin is no longer known and it gets the default first crumb rather than propagating provenance
 * through a second hop.
 *
 * The glossary and the chain both come from the loaded attribute, so until that resolves only the first
 * crumb is inserted and the rest slides in after — as the dataset and entity ancestor padding above do.
 *
 * @param {Array} breadcrumbs - The crumbs produced by useBreadcrumbs.
 * @param {object} attribute - The attribute on screen, from the currentAttribute subject.
 * @param {{ attribute_id: number, ancestors: object[] }} attributeAncestors - Resolved parent entities.
 * @param {string|object} [from] - Where this page was navigated from. A pathname when it came from a
 *   row link; PrivateRoute uses the same key for a whole location object, hence the type check.
 * @returns {Array} The rewritten crumb list, or the original when there is no attribute crumb.
 */
const padBreadcrumbsWithGlossaryTrail = (breadcrumbs, attribute, attributeAncestors, from) => {
  const index = breadcrumbs.findIndex(({ match }) => match.pathname === '/attributes');
  if (index === -1) return breadcrumbs;

  // The attribute list is a tab of the Glossaries page, so arriving from that exact path means the list
  // was the way in. The page's other tab lists glossaries and links to glossaries, not to attributes, so
  // there is nothing else this can be. A row link from inside a glossary carries that glossary's path.
  const cameFromList = typeof from === 'string' && from === '/glossaries';

  // Only trust the loaded attribute and chain when they are the ones this trail is for (guards against
  // a stale response from a previously viewed attribute). Covers the attribute value route too, whose
  // crumbs carry the same attribute_id.
  const attributeId = breadcrumbs.find(({ match }) => match.params?.attribute_id)?.match.params
    .attribute_id;
  const isCurrent = String(attribute?.attribute_id) === String(attributeId);
  const showsGlossary = attribute?.glossary_id && isCurrent;

  return [
    ...breadcrumbs.slice(0, index),
    cameFromList
      ? {
          key: 'all-attributes',
          // The tab is read from the query string. The crumb's Link carries defaultParams as state,
          // where tab is undefined, so it does not override what is in the URL.
          match: { pathname: `/glossaries?tab=${Asset.Attribute}` },
          breadcrumb: 'All Attributes',
        }
      : {
          key: 'glossaries',
          match: { pathname: '/glossaries' },
          breadcrumb: 'Glossaries',
        },
    ...(showsGlossary
      ? [
          {
            key: `attribute-glossary-${attribute.glossary_id}`,
            match: { pathname: `/glossaries/${attribute.glossary_id}` },
            breadcrumb: <GlossaryCrumbContent name={attribute.glossary_name} />,
          },
        ]
      : []),
    // An entity crumb links into a glossary, so it is only added once the glossary crumb is there.
    ...(showsGlossary && String(attributeAncestors?.attribute_id) === String(attributeId)
      ? (attributeAncestors?.ancestors || []).map(ancestor => ({
          key: `attribute-entity-ancestor-${ancestor.entity_id}`,
          match: { pathname: `/glossaries/${attribute.glossary_id}/${ancestor.entity_id}` },
          breadcrumb: <EntityCrumbContent name={ancestor.entity_name} />,
        }))
      : []),
    ...breadcrumbs.slice(index + 1),
  ];
};

export const Breadcrumbs = () => {
  const { defaultParams } = useQueryParams();
  const rawBreadcrumbs = useBreadcrumbs(routesConfig);
  const location = useLocation();

  const { showTooltip, hideTooltip } = useTooltip();

  /* Must do this to "load" the subject */
  const [currentEntity] = useGlobalState('currentEntity');
  const [currentEntityAncestors] = useGlobalState('currentEntityAncestors');
  const [currentConnection] = useGlobalState('currentConnection');
  const [currentCustomProperty] = useGlobalState('currentCustomProperty');
  const [currentDataset] = useGlobalState('currentDataset');
  const [currentDatasetAncestors] = useGlobalState('currentDatasetAncestors');
  const [currentDatasetGroup] = useGlobalState('currentDatasetGroup');
  const [currentDatasource] = useGlobalState('currentDatasource');
  const [currentField] = useGlobalState('currentField');
  const [currentAttribute] = useGlobalState('currentAttribute');
  const [currentAttributeAncestors] = useGlobalState('currentAttributeAncestors');
  const [currentAttributeValue] = useGlobalState('currentAttributeValue');
  const [currentGlossary] = useGlobalState('currentGlossary');
  const [currentSchedule] = useGlobalState('currentSchedule');
  const [currentSystem] = useGlobalState('currentSystem');
  const [currentTask] = useGlobalState('currentTask');
  const [currentUser] = useGlobalState('currentUser');
  const [currentUserGroup] = useGlobalState('currentUserGroup');

  // Pad the trail with parent crumbs when viewing a nested dataset/field or a nested entity.
  let breadcrumbs = padBreadcrumbsWithAncestors(rawBreadcrumbs, currentDatasetAncestors);
  breadcrumbs = padBreadcrumbsWithEntityAncestors(breadcrumbs, currentEntityAncestors);
  // ...and re-root an attribute under its own glossary and parent entities.
  breadcrumbs = padBreadcrumbsWithGlossaryTrail(
    breadcrumbs,
    currentAttribute,
    currentAttributeAncestors,
    location.state?.from
  );

  return (
    <div className="Breadcrumbs">
      {/*          
      <Link className="breadcrumb-item link" to="/">
        <Icon name='katalogue' tooltip='Home' />
      </Link>
      */}

      {/*breadcrumbs.length === 1 &&
        <span className="breadcrumb-item-container">
          <span className="default-breadcrumb">
            katalogue
          </span>
        </span>*/}
      {breadcrumbs.map(({ match, breadcrumb }, key) =>
        key === 0 ? (
          ''
        ) : key + 1 === breadcrumbs.length ? (
          <span key={key} className="breadcrumb-item-container">
            <span
              key={match.pathname}
              className="breadcrumb-item"
              onMouseEnter={() => showTooltip({ bodyText: breadcrumb })}
              onMouseLeave={hideTooltip}
            >
              {breadcrumb}
            </span>
          </span>
        ) : (
          <span key={key} className="breadcrumb-item-container">
            <Link
              key={match.pathname}
              className="breadcrumb-item link"
              to={match.pathname}
              state={{ ...defaultParams, from: location.pathname }}
              onMouseEnter={() => showTooltip({ bodyText: breadcrumb })}
              onMouseLeave={hideTooltip}
            >
              {breadcrumb}
            </Link>
            <span className="breadcrumb-item divider">{'>'}</span>
          </span>
        )
      )}
    </div>
  );
};
